import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2, Eye, Plus, Sparkles, X, XCircle } from "lucide-react";
import type { JSX } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "@/api/errors";
import type { components } from "@/api/schema";
import { toast } from "@/shared/lib/toast";

import { useSimulate } from "../api/use-simulate";
import {
  simulateCartSchema,
  type SimulateCartInput,
  type SimulateCartValues,
} from "../lib/simulate-schema";

type SimulateResponse = components["schemas"]["SimulateResponse"];

interface SimulatePanelProps {
  promotionId: string;
  /**
   * Persists the current builder edits BEFORE simulating so the run evaluates the
   * user's unsaved rules, not the stale persisted promotion. Resolves `false`
   * when the save was blocked/errored (the builder toasted why) → run aborts.
   * For brand-new promotions this also creates the draft before the first simulate.
   */
  onBeforeRun?: () => Promise<boolean>;
}

const CART_INPUT =
  "h-8 w-full rounded-md border border-[var(--premium-border-strong)] bg-white/80 px-2 text-[12px] outline-none transition focus:border-[var(--brand-500)] focus:bg-white focus-ring";
const CART_HEAD = "text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--text-subtle)]";

function emptyLine(): SimulateCartInput["lines"][number] {
  return { sku: "", quantity: 1, unit_price: "0.00" };
}

export function SimulatePanel({ promotionId, onBeforeRun }: SimulatePanelProps): JSX.Element {
  const { t } = useTranslation("promotion");
  const simulate = useSimulate(promotionId);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SimulateCartInput, unknown, SimulateCartValues>({
    resolver: zodResolver(simulateCartSchema),
    defaultValues: { lines: [emptyLine()], discount: "0.00", shipping: "0.00", is_cod: false },
  });
  const lines = useFieldArray({ control, name: "lines" });

  // Live cart total mirrors the BE math (subtotal − discount + shipping) so the
  // operator sees what the buyer pays before running the simulation.
  const watchedLines = useWatch({ control, name: "lines" });
  const watchedDiscount = useWatch({ control, name: "discount" });
  const watchedShipping = useWatch({ control, name: "shipping" });
  const subtotal = (watchedLines ?? []).reduce(
    (sum, l) => sum + (Number(l?.quantity) || 0) * (Number(l?.unit_price) || 0),
    0,
  );
  const total =
    Math.max(0, subtotal - (Number(watchedDiscount) || 0)) + (Number(watchedShipping) || 0);

  async function onSubmit(cart: SimulateCartValues): Promise<void> {
    // Save-first: persist current builder edits so simulate evaluates the
    // just-saved promotion. A blocked/errored save (false) aborts — its own toast
    // explained why.
    const ok = onBeforeRun ? await onBeforeRun() : true;
    if (!ok) return;
    simulate.mutate(cart, {
      onError: (err) => toast.error(apiErrorMessage(err)),
    });
  }

  const outcome: SimulateResponse | undefined = simulate.data;

  return (
    <div
      data-testid="simulate-panel"
      className="rounded-2xl border border-[var(--brand-100)] p-4"
      style={{
        background:
          "linear-gradient(160deg, color-mix(in srgb, var(--brand-50) 70%, white), white)",
      }}
    >
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate className="space-y-3">
        <h3 className="flex items-center gap-2 text-[13px] font-extrabold text-[var(--brand-700)]">
          <Eye className="h-4 w-4" aria-hidden />
          {t("simulate.title")}
        </h3>

        <div className="space-y-1.5">
          <div className="grid grid-cols-[1fr_52px_72px_24px] items-center gap-1.5">
            <span className={CART_HEAD}>{t("simulate.sku")}</span>
            <span className={`${CART_HEAD} text-center`}>{t("simulate.quantity")}</span>
            <span className={`${CART_HEAD} text-center`}>{t("simulate.unit_price")}</span>
            <span />
          </div>
          {lines.fields.map((field, i) => (
            <div
              key={field.id}
              className="grid grid-cols-[1fr_52px_72px_24px] items-center gap-1.5"
              data-testid={`simulate-line-${i}`}
            >
              <input
                aria-label={t("simulate.sku")}
                data-testid={`simulate-sku-${i}`}
                className={CART_INPUT}
                {...register(`lines.${i}.sku`)}
              />
              <input
                type="number"
                min={1}
                aria-label={t("simulate.quantity")}
                data-testid={`simulate-qty-${i}`}
                className={`${CART_INPUT} text-center`}
                {...register(`lines.${i}.quantity`, { valueAsNumber: true })}
              />
              <input
                type="number"
                min={0}
                aria-label={t("simulate.unit_price")}
                data-testid={`simulate-unit-price-${i}`}
                className={`${CART_INPUT} text-right`}
                {...register(`lines.${i}.unit_price`)}
              />
              <button
                type="button"
                aria-label={t("simulate.remove_line")}
                data-testid={`simulate-remove-line-${i}`}
                disabled={lines.fields.length <= 1}
                onClick={() => lines.remove(i)}
                className="grid h-6 w-6 place-items-center rounded text-[var(--text-subtle)] transition hover:text-[var(--intent-danger-fg)] disabled:opacity-40"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          ))}
          {errors.lines?.message && (
            <p
              className="text-xs text-[var(--intent-danger-fg)]"
              data-testid="simulate-lines-error"
            >
              {t(errors.lines.message)}
            </p>
          )}
          <button
            type="button"
            data-testid="simulate-add-line"
            onClick={() => lines.append(emptyLine())}
            className="inline-flex h-7 w-full items-center justify-center gap-1 rounded-md border border-[var(--premium-border-strong)] bg-white/80 text-[12px] font-semibold text-[var(--text-secondary)] transition hover:bg-white"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden /> {t("simulate.add_line")}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={CART_HEAD}>{t("simulate.discount")}</label>
            <input
              type="number"
              min={0}
              data-testid="simulate-discount"
              className={`${CART_INPUT} mt-1`}
              {...register("discount")}
            />
          </div>
          <div>
            <label className={CART_HEAD}>{t("simulate.shipping")}</label>
            <input
              type="number"
              min={0}
              data-testid="simulate-shipping"
              className={`${CART_INPUT} mt-1`}
              {...register("shipping")}
            />
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-[var(--brand-100)] pt-2 text-[12px] font-semibold text-[var(--text-secondary)]">
          <span>{t("simulate.total_paid")}</span>
          <span className="num tabular-nums text-[14px] font-bold text-[var(--text-primary)]">
            ฿{total.toLocaleString()}
          </span>
        </div>

        <label className="flex items-center gap-2 text-[12px] font-semibold text-[var(--text-secondary)]">
          <Controller
            control={control}
            name="is_cod"
            render={({ field }) => (
              <input
                type="checkbox"
                data-testid="simulate-is-cod"
                checked={field.value ?? false}
                onChange={(e) => field.onChange(e.target.checked)}
                onBlur={field.onBlur}
              />
            )}
          />
          {t("simulate.is_cod")}
        </label>

        <button
          type="submit"
          data-testid="simulate-run"
          disabled={simulate.isPending}
          className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-[var(--control-r)] text-[13px] font-bold text-white transition hover:-translate-y-px disabled:translate-y-0 disabled:opacity-50"
          style={{
            background: "var(--grad-primary-btn)",
            boxShadow: "0 1px 2px rgba(15,98,254,.5),0 4px 12px -3px rgba(15,98,254,.45)",
          }}
        >
          <Sparkles className="h-4 w-4" aria-hidden />
          {t("simulate.run")}
        </button>
      </form>

      <div className="mt-3" data-testid="simulate-result">
        {!outcome ? (
          <div
            className="flex flex-col items-center gap-1 py-2 text-center"
            data-testid="simulate-empty"
          >
            <Sparkles className="h-5 w-5 text-[var(--brand-500)]" aria-hidden />
            <p className="text-[12.5px] font-bold text-[var(--text-secondary)]">
              {t("simulate.empty_title")}
            </p>
            <p className="text-[12px] text-[var(--text-subtle)]">{t("simulate.empty")}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <h4 className="text-[11px] font-bold uppercase tracking-[0.05em] text-[var(--text-subtle)]">
              {t("simulate.fired_rules")}
            </h4>
            {outcome.fired.length === 0 ? (
              <p data-testid="simulate-no-fire" className="text-[13px] text-[var(--text-subtle)]">
                {t("simulate.no_fire")}
              </p>
            ) : (
              outcome.fired.map((rule, ri) => (
                <div
                  key={rule.rule_id}
                  data-testid={`simulate-fired-rule-${ri}`}
                  className="rounded-xl border border-[var(--premium-border)] bg-white p-3"
                >
                  <p className="mb-2 text-[13px] font-bold text-[var(--text-primary)]">
                    {t("precheck.rule_prefix", { n: rule.rule_sequence })} — {rule.reason}
                  </p>
                  <div
                    data-testid={`simulate-min-spend-${ri}`}
                    className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-[var(--text-secondary)]"
                  >
                    <span>
                      {t("simulate.subtotal")}:{" "}
                      <span className="num tabular-nums">{rule.min_spend.subtotal}</span>
                    </span>
                    <span>
                      {t("simulate.threshold")}:{" "}
                      <span className="num tabular-nums">{rule.min_spend.threshold}</span>
                    </span>
                    <span>
                      {t("simulate.base")}:{" "}
                      <span className="num tabular-nums">{rule.min_spend.base}</span>
                    </span>
                    <span className="inline-flex items-center gap-1 font-semibold">
                      {rule.min_spend.passed ? (
                        <>
                          <CheckCircle2
                            className="h-3.5 w-3.5 text-[var(--intent-success-fg)]"
                            aria-hidden
                          />
                          {t("simulate.passed")}
                        </>
                      ) : (
                        <>
                          <XCircle
                            className="h-3.5 w-3.5 text-[var(--intent-danger-fg)]"
                            aria-hidden
                          />
                          {t("simulate.failed")}
                        </>
                      )}
                    </span>
                  </div>
                  <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.05em] text-[var(--text-subtle)]">
                    {t("simulate.gifts_awarded")}
                  </p>
                  <ul className="space-y-1">
                    {rule.gifts.map((g, gi) => (
                      <li
                        key={`${g.sku}-${gi}`}
                        data-testid={`simulate-gift-${ri}-${gi}`}
                        className="flex items-center gap-2 rounded-md bg-[var(--surface-sunken)] px-2 py-1 text-[12.5px]"
                      >
                        <span className="font-mono font-semibold">{g.sku}</span>
                        <span className="text-[var(--text-subtle)]">× {g.quantity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
